import React, {ReactElement} from 'react'
import { Box, Stack, useTheme, useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'
import TimeRangeSelect from './TimeRangeSelect'
import YearRangeSelect from './YearRangeSelect'
import DateFilter from 'components/DateFilter/DateFilter'
import { FiltersContainer } from './FiltersStyles'
import { useGetStatsTimeRange, useYearRange } from '../../Traffic/store'
import { TimeRange } from 'models'

import OfferSelect from './OfferSelect'
import MonthSelect from './MonthSelect'

interface FiltersProps {
  section: string
  onOfferChange?: (selectedOffers: string[]) => void
  onDateChange?: (start: Date | null, end: Date | null) => void
  selectedOffers?: string[]
  components?: ReactElement[]
  startDate?: Date | null
  endDate?: Date | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  offerDistribution?: any[]
  isSmall?: boolean
  isEvolutionMode?: boolean
}

const Filters: React.FC<FiltersProps> = ({
  section,
  onOfferChange,
  onDateChange,
  components = [],
  selectedOffers = [],
  startDate,
  endDate,
  offerDistribution,
  isSmall = false,
  isEvolutionMode = false
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [timeRange, setTimeRange] = useGetStatsTimeRange()
  const [yearRange, setYearRange] = useYearRange()

  const handleOfferChange = (newOffers: string[]) => {
    onOfferChange && onOfferChange(newOffers)
  }

  const handleDateChange = (start: Date | null, end: Date | null) => {
    onDateChange && onDateChange(start, end)
  }

  const handleMonthSelect = (selectedDate: Date) => {
    const start = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
    const end = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0)
    handleDateChange(start, end)
  }

  const evolutionChoices = [
    {
      label: t('stats.range_select.last_12_months'),
      value: TimeRange.LastTwelveMonths,
      id: 1,
    }
  ]

  const isPurchaseOrCodes = ['codes', 'purchases'].includes(section)

  return (
    <FiltersContainer>
      <Stack direction={'row'} gap={1} height={'100%'} flexWrap={'wrap'} sx={{ width: '100%', '& > *': { minWidth: { xs: isPurchaseOrCodes ? 0 : '100%', sm: 'auto' } } }}>
        {['globals'].includes(section) && (
          <Stack>
            <TimeRangeSelect
              value={isEvolutionMode ? TimeRange.LastTwelveMonths : timeRange}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(newValue: any) => {
                if (!isEvolutionMode) {
                  setTimeRange(newValue.value)
                }
              }}
              choices={isEvolutionMode ? evolutionChoices : undefined}
              isSmall={isSmall || isMobile}
              disabled={isEvolutionMode}
            />
          </Stack>
        )}
        {['codes', 'purchases', 'monthly'].includes(section) && (
          <Box sx={isPurchaseOrCodes ? { flex: { xs: '0 0 calc(50% - 4px)', sm: 'auto' }, minWidth: 0 } : {}}>
            <YearRangeSelect
              value={yearRange}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(newValue: any) => setYearRange(newValue.value)}
            />
          </Box>
        )}
        {['codes', 'purchases', 'customers'].includes(section) && (
          <>
            {onDateChange && (
              <Box sx={{ flex: { sm: '1 1 0', xs: '0 0 100%' }, minWidth: { xs: '100%', sm: 0 } }}>
                <DateFilter
                  onReset={() => handleDateChange(null, null)}
                  startDate={startDate || null}
                  endDate={endDate || null}
                  onSubmit={handleDateChange}
                  placeholderKey={'stats.traffic.date_filter'}
                  showPresets={true}
                  blockFutureDates={true}
                />
              </Box>
            )}
            {offerDistribution && onOfferChange && (
              <Box sx={{ width: { sm: '300px', xs: isPurchaseOrCodes ? 'calc(50% - 4px)' : '100%' }, flexShrink: 0, minWidth: 0 }}>
                <OfferSelect
                  onChange={handleOfferChange}
                  values={selectedOffers}
                  offers={offerDistribution}
                />
              </Box>
            )}
          </>
        )}
        {section === 'chromecasts' && (
          <MonthSelect
            onMonthSelect={handleMonthSelect}
            startDate={new Date(2018, 0)}
            endDate={new Date()}
          />
        )}
        {
          components
        }
      </Stack>
    </FiltersContainer>
  )
}

export default Filters